import { Box, Image, Theme, Typography } from "@outschool/backpack";
import { useTrackEvent } from "@outschool/ui-analytics";
import { Container } from "@outschool/ui-legacy-component-library";
import { useIsMobile, useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import { LinkGridProps, LinkGridPropsItem } from "./types";

export default function CardLinkGrid({
  title,
  items: allItems,
  renderImage,
}: LinkGridProps) {
  const trackEvent = useTrackEvent();
  const Link = useLinkComponent();
  const isMobile = useIsMobile();
  const aspectRatio = getAspectRatio(allItems);

  // Only support an even number of items for now
  if (allItems.length % 2 !== 0) {
    allItems.pop();
  }

  let items: LinkGridProps["items"];
  if (isMobile && aspectRatio === "5:3") {
    items = allItems.slice(0, 6);
  } else {
    items = allItems.slice(0, 4);
  }

  return (
    <Container fullBleed={true}>
      <Typography
        variant="h2"
        sx={{ textAlign: "center", marginBottom: "32px" }}
      >
        {title}
      </Typography>
      <Box
        flex
        sx={(theme: Theme) => ({
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "16px",
          justifyContent: "space-between",
          [theme.breakpoints.down("lg")]: {
            justifyContent: "center",
          },
          [theme.breakpoints.down("sm")]: {
            justifyContent: "space-between",
          },
        })}
      >
        {items.map((item: LinkGridPropsItem, index: number) => (
          <Box
            as={Link}
            key={index + item.path}
            flex
            sx={(theme: Theme) => ({
              flexDirection: "column",
              overflow: "hidden",
              border: "1px solid",
              borderColor: theme.palette.grey["100"],
              borderRadius: "20px",
              textDecoration: "none",
              color: theme.palette.grey["900"],
              "&:hover:not(:disabled)": {
                textDecoration: "none",
                color: "black",
              },
              [theme.breakpoints.down(769)]: {
                width: "47%",
              },
              [theme.breakpoints.down(320)]: {
                width: "45%",
              },
              [theme.breakpoints.up(769)]: {
                width: "23%",
              },
            })}
            to={item.path}
            onClick={() => {
              trackEvent("home_page_card_grid_click", {
                gridTitle: title,
                cardText: item.text,
                cardPath: item.path,
              });
            }}
          >
            <Box
              flex
              sx={{
                overflow: "hidden",
                borderRadius: "20px 20px 0 0",
                position: "relative",
                aspectRatio: aspectRatio.replace(":", "/"),
              }}
            >
              {renderImage ? (
                renderImage(
                  {
                    alt: item.imageAltText,
                    src: item.image.url,
                    sizes: "(max-width: 768px) 50vw, 10vw",
                  },
                  {
                    layout: "fill",
                  },
                )
              ) : (
                <Image
                  alt={item.imageAltText}
                  src={item.image.url}
                  sx={{
                    width: "100%",
                  }}
                />
              )}
            </Box>
            <Box
              flex
              sx={(theme: Theme) => ({
                textAlign: "center",
                justifyContent: "center",
                px: "16px",
                py: "24px",
                [theme.breakpoints.down("md")]: {
                  py: "16px",
                },
              })}
            >
              <Typography variant="h6">{item.text}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Container>
  );
}

function getAspectRatio(items: LinkGridProps["items"]): string {
  const aspectRatio = items?.[0]?.imageAspectRatio;

  if (!aspectRatio) {
    return "1:1";
  }

  return aspectRatio === "square" ? "1:1" : aspectRatio;
}
